import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Flex } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { displayDateFormat } from "tm-shared/constants";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { CameraModeModal } from "@/components/Modals/CameraModeModal";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { APP_VERSION } from "@/config";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useMultiTap } from "@/hooks/useMultiTap";
import { logoutService } from "@/Services/Logout";
import { photoStorage, PhotoStorageKind } from "@/Services/PhotoStorage";
import { saveProgressToServerThunk } from "@/store/thunks";

import { ProgressSync } from "./components/ProgressSync";
import { UserInfo } from "./components/UserInfo";
import { UserSettings } from "./components/UserSettings";

const MainInformation = () => {
    const { email, firstName, lastName, id } = useAppSelector(
        (state) => state.user,
    );
    const {
        progress: { lastSyncedAt },
    } = useAppSelector((state) => state.offline);

    const [isCameraModeModalOpen, setIsCameraModeModalOpen] = useState(false);
    const [storageKind, setStorageKind] = useState<PhotoStorageKind | null>(
        null,
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        const getStorageKind = async () => {
            const kind = await photoStorage.getKind();

            setStorageKind(kind);
        };

        getStorageKind();
    }, []);

    const [logoutHandler, logoutLoading] = useAsyncAction(async () => {
        localStorage.setItem("prevUserId", id);

        try {
            await dispatch(saveProgressToServerThunk()).unwrap();
        } catch (error) {
            console.error(error);
        }

        await logoutService();
    });

    const lastSyncDate = dayjs(lastSyncedAt);
    const lastSyncDisplay = dayjs().isSame(lastSyncDate, "day")
        ? lastSyncDate.format(displayDateFormat.time)
        : lastSyncDate.format(displayDateFormat.dateTime);

    const handleOpenChangeCameraModal = useMultiTap(
        () => {
            setIsCameraModeModalOpen(true);
        },
        {
            taps: 3,
        },
    );

    return (
        <>
            <Meta title="Profile" />
            <Header withMidnightCountdown />

            <MainContainer>
                <Flex gap={8} style={{ marginBottom: 32 }}>
                    <Avatar size={80} icon={<UserOutlined />} />

                    <Flex vertical gap={4}>
                        {email && (
                            <PrimaryText fontSize={16}>{email}</PrimaryText>
                        )}

                        <PrimaryText fontSize={16}>
                            {firstName} {lastName}
                        </PrimaryText>
                    </Flex>
                </Flex>

                <Flex vertical gap={32} style={{ marginBottom: 8 }}>
                    <UserInfo />

                    <UserSettings />

                    <ProgressSync />
                </Flex>

                <Flex vertical gap={8}>
                    <Button
                        type="text"
                        style={{ width: "fit-content", alignSelf: "center" }}
                        onClick={handleOpenChangeCameraModal}
                    >
                        <SecondaryText>v{APP_VERSION}</SecondaryText>
                        {storageKind && (
                            <SecondaryText>{storageKind}</SecondaryText>
                        )}
                    </Button>

                    <BaseButton
                        type="primary"
                        htmlType="submit"
                        icon={<LogoutOutlined />}
                        onClick={logoutHandler}
                        loading={logoutLoading}
                    >
                        Вийти
                    </BaseButton>

                    {lastSyncedAt && (
                        <SecondaryText>
                            Остання синхронізація: {lastSyncDisplay}
                        </SecondaryText>
                    )}
                </Flex>
            </MainContainer>

            <CameraModeModal
                open={isCameraModeModalOpen}
                onClose={() => setIsCameraModeModalOpen(false)}
            />
        </>
    );
};

export default MainInformation;
